import { type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X } from 'lucide-react';
import type { UserFavoriteWithArche } from '@/lib/api';

interface FavoriteCardProps {
  favorite: UserFavoriteWithArche;
  onRemove: (archId: string) => void;
}

export function FavoriteCard({ favorite, onRemove }: FavoriteCardProps) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: favorite.archId,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative flex items-center gap-3 p-4 rounded-xl bg-card border border-border hover:border-primary/50 hover:bg-accent/50 transition-all cursor-pointer"
      onClick={() => navigate(`/a/${favorite.archId}`)}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="flex-shrink-0 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Arche image or placeholder */}
      {favorite.archeImageRef ? (
        <img
          src={favorite.archeImageRef}
          alt={favorite.archeName}
          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <span className="text-lg font-semibold text-primary">{favorite.archeName.charAt(0)}</span>
        </div>
      )}

      {/* Text */}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-foreground truncate">{favorite.archeName}</h3>
        <p className="text-sm text-muted-foreground truncate">{favorite.archeSimpleDescription}</p>
      </div>

      {/* Remove button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(favorite.archId);
        }}
        className="flex-shrink-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all"
        title="Remove from desktop"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
